import * as Sentry from '@sentry/react';
import {
  getLastSentryEventId,
  sentryBrowserTunnelPath,
  SENTRY_USER_REPORT_DIALOG_FR,
  setLastSentryEventId,
} from '@wapharma/shared';

function sentryDsn(): string | undefined {
  return import.meta.env.VITE_SENTRY_DSN?.trim() || undefined;
}

function sentryLogsEnabled(): boolean {
  const raw = import.meta.env.VITE_SENTRY_ENABLE_LOGS?.trim().toLowerCase();
  if (raw === '0' || raw === 'false') return false;
  return true;
}

/** GlitchTip User Reports tab — linked to captured error when eventId present. */
export function openSentryUserReportDialog(eventId?: string | null): boolean {
  const dsn = sentryDsn();
  if (!dsn) return false;

  const resolved =
    eventId?.trim() ||
    getLastSentryEventId() ||
    Sentry.lastEventId() ||
    Sentry.captureMessage('Signalement utilisateur', 'error');

  if (!resolved) return false;

  setLastSentryEventId(resolved);
  try {
    Sentry.showReportDialog({
      dsn,
      eventId: resolved,
      ...SENTRY_USER_REPORT_DIALOG_FR,
    });
    return true;
  } catch {
    return false;
  }
}

export function initAdminSentry(): void {
  const dsn = sentryDsn();
  if (!dsn) return;

  const tracesSampleRateRaw = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.05');
  const tracesSampleRate =
    Number.isFinite(tracesSampleRateRaw) && tracesSampleRateRaw >= 0 && tracesSampleRateRaw <= 1
      ? tracesSampleRateRaw
      : 0.05;

  const enableLogs = sentryLogsEnabled();
  const integrations: ReturnType<typeof Sentry.browserTracingIntegration>[] = [
    Sentry.browserTracingIntegration(),
  ];
  if (enableLogs) {
    integrations.push(Sentry.consoleLoggingIntegration() as ReturnType<typeof Sentry.browserTracingIntegration>);
  }

  Sentry.init({
    dsn,
    tunnel: sentryBrowserTunnelPath(import.meta.env.VITE_API_URL),
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE?.trim() || undefined,
    integrations,
    tracesSampleRate,
    enableLogs,
    beforeSend(event: Sentry.ErrorEvent) {
      if (event.event_id) setLastSentryEventId(event.event_id);
      return event;
    },
  });
}

export { Sentry };
